import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, DollarSign, HelpCircle, MoreHorizontal, X } from "lucide-react";

/**
 * VisitorMobileBottomNav - Fixed bottom navigation bar for Visitor Mode on mobile devices
 * Only visible on screens < 768px
 * 
 * Layout: Home | Pricing | FAQ | More
 * More menu expands to show: About, Contact, Sign In, Get Started
 */
const VisitorMobileBottomNav = () => {
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => {
    return location.pathname === path;
  };

  const closeMoreMenu = () => {
    setMoreMenuOpen(false);
  };

  return (
    <>
      {/* Fixed Bottom Navigation Bar - Mobile Only */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-slate-950/95 backdrop-blur-md border-t border-slate-800">
        <div className="flex items-center justify-around h-16 px-2">
          {/* Welcome - Only show if not on home page */}
          {!isActive("/") && (
            <Link
              to="/"
              className="flex flex-col items-center justify-center flex-1 h-full transition-colors text-slate-400 hover:text-slate-200"
            >
              <Home className="w-5 h-5 mb-1" />
              <span className="text-xs font-medium">Welcome</span>
            </Link>
          )}

          {/* Pricing - Only show if not on pricing page */}
          {!isActive("/pricing") && (
            <Link
              to="/pricing"
              className="flex flex-col items-center justify-center flex-1 h-full transition-colors text-slate-400 hover:text-slate-200"
            >
              <DollarSign className="w-5 h-5 mb-1" />
              <span className="text-xs font-medium">Pricing</span>
            </Link>
          )}

          {/* FAQ - Only show if not on FAQ page */}
          {!isActive("/faq") && (
            <Link
              to="/faq"
              className="flex flex-col items-center justify-center flex-1 h-full transition-colors text-slate-400 hover:text-slate-200"
            >
              <HelpCircle className="w-5 h-5 mb-1" />
              <span className="text-xs font-medium">FAQ</span>
            </Link>
          )}

          {/* More - Always show */}
          <button
            onClick={() => setMoreMenuOpen(!moreMenuOpen)}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              moreMenuOpen
                ? "text-purple-400"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            <MoreHorizontal className="w-5 h-5 mb-1" />
            <span className="text-xs font-medium">More</span>
          </button>
        </div>
      </nav>

      {/* More Menu Panel - Slides up from bottom */}
      {moreMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="md:hidden fixed inset-0 bg-black/60 z-40"
            onClick={closeMoreMenu}
          />

          {/* Menu Panel */}
          <div className="md:hidden fixed bottom-16 left-0 right-0 z-50 bg-slate-900/98 backdrop-blur-lg border-t border-slate-700 rounded-t-2xl shadow-2xl animate-slide-up">
            <div className="p-6">
              {/* Close button */}
              <button
                onClick={closeMoreMenu}
                className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <h3 className="text-lg font-semibold text-white mb-4">More Options</h3>

              <div className="space-y-2">
                {/* About - Only show if not on about page */}
                {!isActive("/about") && (
                  <Link
                    to="/about"
                    onClick={closeMoreMenu}
                    className="block px-4 py-3 text-base font-medium text-slate-200 hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    About NewAudia
                  </Link>
                )}

                {/* Contact - Only show if not on contact page */}
                {!isActive("/contact") && (
                  <Link
                    to="/contact"
                    onClick={closeMoreMenu}
                    className="block px-4 py-3 text-base font-medium text-slate-200 hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    Contact Us
                  </Link>
                )}

                {/* Divider - Only show if we have links above */}
                {(!isActive("/about") || !isActive("/contact")) && (
                  <div className="border-t border-slate-700 my-3" />
                )}

                {/* Sign In */}
                <Link
                  to="/login"
                  onClick={closeMoreMenu}
                  className="block px-4 py-3 text-base font-medium bg-slate-800 text-slate-100 hover:bg-slate-700 rounded-lg transition-colors text-center"
                >
                  Sign In
                </Link>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add animation styles */}
      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default VisitorMobileBottomNav;
